import { BrowserFileUtils } from '../utils/BrowserFileUtils'

/**
 * 浏览器兼容的文件读取器
 * 使用 ArrayBuffer/Uint8Array 替代 Node.js Buffer
 */
export class BrowserFileReader {
  public start: number = 0
  public bytes: Uint8Array = new Uint8Array()
  private _isValid: boolean = true
  private view: DataView = new DataView(new ArrayBuffer(0))

  /**
   * 从 ArrayBuffer 或 TypedArray 创建读取器，兼容旧安卓 WebView 跨上下文对象。
   */
  constructor(source: Uint8Array | ArrayBuffer) {
    this.bytes = BrowserFileReader.normalizeSource(source)
    this.refreshView()
  }

  private static normalizeSource(source: unknown): Uint8Array {
    if (!source) {
      throw new Error('二进制读取器初始化失败：数据为空')
    }
    if (source instanceof Uint8Array) {
      return source
    }
    if (source instanceof ArrayBuffer || Object.prototype.toString.call(source) === '[object ArrayBuffer]') {
      return new Uint8Array(source as ArrayBuffer)
    }
    const view = source as {
      buffer?: ArrayBuffer
      byteOffset?: number
      byteLength?: number
    }
    if (
      view.buffer
      && typeof view.byteLength === 'number'
      && typeof view.byteOffset === 'number'
    ) {
      return new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
    }
    throw new Error('二进制读取器初始化失败：不是有效的 ArrayBuffer 或 Uint8Array')
  }

  /**
   * 从 File 对象异步创建读取器
   */
  public static async fromFile(file: File, start?: number, length?: number): Promise<BrowserFileReader> {
    const buffer = await BrowserFileUtils.blobToArrayBuffer(file)
    let bytes = new Uint8Array(buffer)
    
    if (start !== undefined && start > 0) {
      const end = length !== undefined ? start + length : bytes.length
      bytes = bytes.slice(start, end)
    } else if (length !== undefined && length > 0) {
      bytes = bytes.slice(0, length)
    }
    
    return new BrowserFileReader(bytes)
  }

  /**
   * 从 Blob 对象异步创建读取器
   */
  public static async fromBlob(blob: Blob, start?: number, length?: number): Promise<BrowserFileReader> {
    const buffer = await BrowserFileUtils.blobToArrayBuffer(blob)
    let bytes = new Uint8Array(buffer)
    
    if (start !== undefined && start > 0) {
      const end = length !== undefined ? start + length : bytes.length
      bytes = bytes.slice(start, end)
    } else if (length !== undefined && length > 0) {
      bytes = bytes.slice(0, length)
    }
    
    return new BrowserFileReader(bytes)
  }

  /**
   * 裁剪字节
   */
  public sliceBytes(start: number, length: number): void {
    if (start >= 0 && length >= 0) {
      this.bytes = this.bytes.subarray(start, start + length)
      this.start = 0
      this.refreshView()
    }
  }

  /**
   * 检查是否有效
   */
  private checkValid(): boolean {
    return this._isValid && this.bytes.length > 0
  }

  private refreshView(): void {
    this.view = new DataView(this.bytes.buffer, this.bytes.byteOffset, this.bytes.byteLength)
  }

  private describeReadFailure(operation: string, length: number): string {
    return `${operation} 读取越界：${length}`
  }

  private assertReadable(length: number, operation: string): void {
    if (!Number.isFinite(length) || length < 0) {
      throw new Error(this.describeReadFailure(`${operation} 长度非法`, length))
    }
    if (!this.checkValid() || this.start + length > this.bytes.length) {
      throw new Error(this.describeReadFailure(operation, length))
    }
  }

  /**
   * 读取字符串 (先读取长度，再读取内容)
   */
  public readString(): string {
    const length = this.readInt()
    if (length === 0) return ""
    this.assertReadable(length, 'readString.body')
    return BrowserFileUtils.bytesToString(this.readSome(length))
  }

  /**
   * 读取固定长度字符串
   */
  public readStringLen(length: number): string {
    this.assertReadable(length, 'readStringLen')
    return BrowserFileUtils.bytesToString(this.readSome(length))
  }

  /**
   * 读取 MS 格式字符串 (6字节)
   */
  public readMSString(): string {
    this.assertReadable(BrowserFileUtils.MSSTRING_LENGTH, 'readMSString')
    return BrowserFileUtils.bytesToString(this.readSome(BrowserFileUtils.MSSTRING_LENGTH))
  }

  /**
   * 读取 int32
   */
  public readInt(): number {
    this.assertReadable(BrowserFileUtils.INT_LENGTH, 'readInt')
    const value = this.view.getInt32(this.start, true)
    this.start += BrowserFileUtils.INT_LENGTH
    return value
  }

  /**
   * 读取 uint32
   */
  public readUInt(): number {
    this.assertReadable(BrowserFileUtils.INT_LENGTH, 'readUInt')
    const value = this.view.getUint32(this.start, true)
    this.start += BrowserFileUtils.INT_LENGTH
    return value
  }

  /**
   * 读取 int32 (Big Endian)
   */
  public readInt32BE(): number {
    this.assertReadable(BrowserFileUtils.INT_LENGTH, 'readInt32BE')
    const value = this.view.getInt32(this.start, false)
    this.start += BrowserFileUtils.INT_LENGTH
    return value
  }

  /**
   * 读取 uint16
   */
  public readUInt16(): number {
    this.assertReadable(BrowserFileUtils.INT16_LENGTH, 'readUInt16')
    const value = this.view.getInt16(this.start, false)
    this.start += BrowserFileUtils.INT16_LENGTH
    return value
  }

  /**
   * 读取 uint64
   */
  public readULong64(): bigint {
    this.assertReadable(BrowserFileUtils.DOUBLE_LENGTH, 'readULong64')
    const value = this.view.getBigUint64(this.start, true)
    this.start += BrowserFileUtils.DOUBLE_LENGTH
    return value
  }

  /**
   * 读取 UTF16BE 字符串
   */
  public readStringUTF16BE(len?: number): string {
    if (len !== undefined) {
      const bytes = this.readSome(len)
      // Swap bytes for UTF16BE
      const swapped = new Uint8Array(bytes.length)
      for (let i = 0; i < bytes.length; i += 2) {
        swapped[i] = bytes[i + 1]
        swapped[i + 1] = bytes[i]
      }
      return new TextDecoder('utf-16le').decode(swapped)
    } else {
      len = this.readInt()
      return this.readStringUTF16BE(len)
    }
  }

  /**
   * 读取 UTF16LE 字符串
   */
  public readStringUTF16LE(len?: number): string {
    if (len !== undefined) {
      const bytes = this.readSome(len)
      return new TextDecoder('utf-16le').decode(bytes)
    } else {
      len = this.readInt()
      return this.readStringUTF16LE(len)
    }
  }

  /**
   * 读取 Unicode 字符
   */
  public readUString(): string {
    const array = this.readSome(2)
    const view = new DataView(array.buffer, array.byteOffset, array.byteLength)
    return String.fromCharCode(view.getUint16(0, true))
  }

  /**
   * 读取指定长度字节
   */
  public readSome(len: number): Uint8Array {
    this.assertReadable(len, 'readSome')
    const end = this.start + len
    const bytes = this.bytes.subarray(this.start, end)
    this.start = end
    return bytes
  }

  /**
   * 读取布尔值
   */
  public readBool(): boolean {
    this.assertReadable(BrowserFileUtils.BOOL_LENGTH, 'readBool')
    return this.bytes[this.start++] !== 0x00
  }

  /**
   * 读取浮点数
   */
  public readFloat(): number {
    this.assertReadable(BrowserFileUtils.INT_LENGTH, 'readFloat')
    const value = this.view.getFloat32(this.start, true)
    this.start += BrowserFileUtils.INT_LENGTH
    return value
  }

  /**
   * 读取双精度浮点数
   */
  public readDouble(): number {
    this.assertReadable(BrowserFileUtils.DOUBLE_LENGTH, 'readDouble')
    const value = this.view.getFloat64(this.start, true)
    this.start += BrowserFileUtils.DOUBLE_LENGTH
    return value
  }

  /**
   * 读取 long (int64)
   */
  public readLong(): number {
    this.assertReadable(BrowserFileUtils.DOUBLE_LENGTH, 'readLong')
    const low = this.view.getUint32(this.start, true)
    const high = this.view.getInt32(this.start + BrowserFileUtils.INT_LENGTH, true)
    this.start += BrowserFileUtils.DOUBLE_LENGTH
    return high * 0x100000000 + low
  }

  /**
   * 读取 int64 (返回 bigint)
   */
  public readInt64(): bigint {
    this.assertReadable(BrowserFileUtils.DOUBLE_LENGTH, 'readInt64')
    const value = this.view.getBigInt64(this.start, true)
    this.start += BrowserFileUtils.DOUBLE_LENGTH
    return value
  }

  /**
   * 获取文件大小
   */
  public getFileSize(): number {
    return this.bytes.length
  }

  /**
   * 是否读取结束
   */
  public isReadEnd(): boolean {
    return this.start >= this.getFileSize()
  }

  /**
   * 读取子对象
   */
  public readObj(length: number): BrowserFileReader {
    return new BrowserFileReader(this.readSome(length))
  }

  /**
   * 跳转到指定位置
   */
  public seek(location: number): void {
    if (location < 0 || location > this.getFileSize()) {
      throw new Error(this.describeReadFailure(`seek(${location})`, 0))
    }
    this.start = location
  }

  /**
   * 跳过指定字节
   */
  public skip(offset: number): void {
    if (offset < 0 || offset > this.getFileSize() - this.start) {
      throw new Error(this.describeReadFailure(`skip(${offset})`, offset))
    }
    this.start += offset
  }

  /**
   * 获取当前位置
   */
  public getPosition(): number {
    return this.start
  }

  /**
   * 获取剩余字节数
   */
  public getRemainingBytes(): number {
    return this.bytes.length - this.start
  }

  /**
   * 关闭读取器
   */
  public close(): void {
    this._isValid = false
    this.bytes = new Uint8Array()
    this.start = 0
    this.refreshView()
  }
}

export default BrowserFileReader
